import { useEffect } from 'react';
import type { SEOMetadata } from '../utils/seoMetadata';
import {
  generateCompanyStructuredData,
  generateWebsiteStructuredData,
} from '../utils/seoMetadata';

export interface BreadcrumbItem {
  name: string;
  path: string; // '/news' のようなサイト内パス
}

interface SEOHeadProps {
  metadata: SEOMetadata;
  currentPath?: string;
  activeSection?: string; // Deprecated: kept for backward compatibility
  /** 渡されたら「ホーム」+ これらでパンくず JSON-LD を作る（記事ページの 3 階層など） */
  breadcrumbs?: readonly BreadcrumbItem[];
  /** 記事ページの NewsArticle JSON-LD。無ければ article スクリプトを消す */
  articleData?: Record<string, unknown>;
}

/**
 * SEOHead component manages document head metadata for SEO optimization
 * Implements requirements 3.1, 3.2, 3.3, 3.4 for comprehensive SEO support
 * Updated to support route-based metadata management
 */
const SEOHead = ({
  metadata,
  currentPath,
  activeSection,
  breadcrumbs,
  articleData,
}: SEOHeadProps) => {
  useEffect(() => {
    // Update document title
    document.title = metadata.title;

    // Update or create meta tags
    const updateMetaTag = (
      name: string,
      content: string,
      property?: boolean
    ) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(
        `meta[${attribute}="${name}"]`
      ) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }

      meta.content = content;
    };

    // Basic meta tags
    updateMetaTag('description', metadata.description);
    updateMetaTag('keywords', metadata.keywords.join(', '));

    // Open Graph Protocol tags
    updateMetaTag('og:title', metadata.ogTitle || metadata.title, true);
    updateMetaTag(
      'og:description',
      metadata.ogDescription || metadata.description,
      true
    );
    updateMetaTag('og:type', metadata.ogType ?? 'website', true);
    updateMetaTag('og:url', metadata.ogUrl || window.location.href, true);
    updateMetaTag('og:site_name', 'eNEW Studio', true);
    updateMetaTag('og:locale', 'ja_JP', true);

    if (metadata.ogImage) {
      updateMetaTag('og:image', metadata.ogImage, true);
      updateMetaTag('og:image:alt', 'eNEW Studio Logo', true);
      updateMetaTag('og:image:width', '1200', true);
      updateMetaTag('og:image:height', '630', true);
    }

    // Twitter Card tags
    updateMetaTag(
      'twitter:card',
      metadata.twitterCard || 'summary_large_image'
    );
    updateMetaTag('twitter:title', metadata.ogTitle || metadata.title);
    updateMetaTag(
      'twitter:description',
      metadata.ogDescription || metadata.description
    );

    if (metadata.ogImage) {
      updateMetaTag('twitter:image', metadata.ogImage);
    }

    // Canonical URL - use metadata.canonicalUrl if provided
    let canonical = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = metadata.canonicalUrl || window.location.href;

    // Language and locale
    document.documentElement.lang = 'ja';

    // Additional meta tags for better SEO
    updateMetaTag(
      'robots',
      metadata.noindex ? 'noindex, follow' : 'index, follow'
    );
    updateMetaTag('author', 'eNEW Studio');
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0');
    updateMetaTag('format-detection', 'telephone=no');

    // JSON-LD Structured Data
    const updateStructuredData = (
      id: string,
      data: Record<string, unknown>
    ) => {
      let script = document.querySelector(
        `script[data-structured-data="${id}"]`
      ) as HTMLScriptElement;

      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-structured-data', id);
        document.head.appendChild(script);
      }

      script.textContent = JSON.stringify(data);
    };

    // Company structured data
    updateStructuredData(
      'company',
      generateCompanyStructuredData() as unknown as Record<string, unknown>
    );

    // Website structured data
    updateStructuredData(
      'website',
      generateWebsiteStructuredData() as unknown as Record<string, unknown>
    );

    // Article structured data (記事ページのみ)
    if (articleData) {
      updateStructuredData('article', articleData);
    } else {
      document
        .querySelector('script[data-structured-data="article"]')
        ?.remove();
    }

    // Breadcrumb structured data
    // Use currentPath for route-based breadcrumbs, fallback to activeSection for backward compatibility
    const shouldShowBreadcrumb = currentPath
      ? currentPath !== '/'
      : activeSection && activeSection !== 'hero';

    if (breadcrumbs && breadcrumbs.length > 0) {
      updateStructuredData('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'ホーム',
            item: 'https://enew.studio',
          },
          ...breadcrumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 2,
            name: crumb.name,
            item: `https://enew.studio${crumb.path}`,
          })),
        ],
      });
    } else if (shouldShowBreadcrumb) {
      const breadcrumbData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'ホーム',
            item: 'https://enew.studio',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: currentPath
              ? getRouteName(currentPath)
              : getSectionName(activeSection || ''),
            item: currentPath
              ? `https://enew.studio${currentPath}`
              : `https://enew.studio#${activeSection}`,
          },
        ],
      };

      updateStructuredData('breadcrumb', breadcrumbData);
    } else {
      // Remove breadcrumb for home page
      const breadcrumbScript = document.querySelector(
        'script[data-structured-data="breadcrumb"]'
      );
      if (breadcrumbScript) {
        breadcrumbScript.remove();
      }
    }
  }, [metadata, currentPath, activeSection, breadcrumbs, articleData]);

  return null; // This component only manages document head
};

/**
 * Get human-readable route name for breadcrumbs
 */
const getRouteName = (path: string): string => {
  const routeNames: Record<string, string> = {
    '/': 'ホーム',
    '/privacy': 'プライバシーポリシー',
    '/contact': 'お問い合わせ',
    '/news': 'お知らせ',
  };

  return routeNames[path] || path;
};

/**
 * Get human-readable section name for breadcrumbs
 * @deprecated Use getRouteName for route-based breadcrumbs
 */
const getSectionName = (sectionId: string): string => {
  const sectionNames: Record<string, string> = {
    company: '会社概要',
    'company-info': '会社情報',
    message: '代表メッセージ',
    services: 'サービス',
    contact: 'お問い合わせ',
    access: 'アクセス',
  };

  return sectionNames[sectionId] || sectionId;
};

export default SEOHead;
